import {
  Menu,
  MenuItem,
  Tooltip,
  Button,
  Checkbox,
  Input,
} from "@mui/material";
import styled from "styled-components/macro";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import DropdownIcon from "@mui/icons-material/ArrowDropDown";
import { usePageInUrl } from "./MachineSwitcherDropdown";
import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useState } from "react";
import { useSelector } from "store/useSelector";
import useNavTitle from "./NavTitle";
import {
  useAnimationsEnabled,
  useGetPageFromPath,
  useInterval,
  useSummaryGroup,
  useVisibleGroups,
  useWorkScheduleIdOnCurrentPage,
} from "utils/customHooks/genericHooks";
import { BREAKPOINT_DESKTOP_XL, useBreakpoints } from "constants/constants";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useDispatch } from "react-redux";
import {
  clearAllAggregates,
  clearAllSegments,
  clearAllTrendChartBars,
} from "providers/GlobalStateProvider";
import { atom, useAtom } from "jotai";
import { snakeCase } from "lodash";
import { animated, useSpring } from "@react-spring/web";
import { usePreviousDistinct } from "react-use";
import {
  BREADCRUMBS_GAP_REMS,
  useBreadcrumbDropdowns,
} from "./useBreadcrumbDropdowns";
import qs from "query-string";
import { useWorkScheduleConfigJsonListAtom } from "store/atoms";
import { ProcessGroup } from "types/types";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
const MAX_GROUP_NAME_LENGTH = 200;

const ProcessGroupSwitcherStyles = styled.h6`
  display: flex;
  justify-content: start;
  flex-grow: 1;
  @media screen and (min-width: ${BREAKPOINT_DESKTOP_XL}px) {
    .dropdownArrow {
      font-size: 36px;
    }
  }
  &.multiSchedules {
    .departmentName {
      line-height: 1.4;
      max-width: 400px;
      font-size: 12px;
      white-space: normal;
    }
  }
`;

export const cyclingThroughProcessGroupsAtom = atom<{
  enabled: boolean;
  cycleTimeMs: number;
}>({
  enabled: false,
  cycleTimeMs: 10 * 1000,
});

export function ProcessGroupSwitcherDropdown() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [{ enabled, cycleTimeMs }, setCyclingThroughProcessGroups] = useAtom(
    cyclingThroughProcessGroupsAtom
  );
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const {
    isTimelinePage,
    isTier0Page,
    isOperatorPage,
    isAllWorkSchedulesPage,
    isGroupTilesPage,
  } = useGetPageFromPath();
  const { translate } = useFlightTranslation();
  const allGroups = useSelector(state => state.legacy.allGroups);
  const navigate = useNavigate();
  const pageInUrl = usePageInUrl();
  const navTitle = useNavTitle();
  const visibleGroups = useVisibleGroups();
  const summaryGroup = useSummaryGroup();
  const { search } = useLocation();
  const processGroup =
    // on tier0 page, process group is the one that has the process whose name is in search under key "process" (so, find the group that has that process)
    isTier0Page
      ? allGroups.find(g =>
          g.processes.find(p => p.name === qs.parse(search).process)
        )
      : visibleGroups.find(g => g.summaryGroup === summaryGroup);

  const processGroupName = processGroup?.longName;
  const dispatch = useDispatch();
  const workScheduleId = useWorkScheduleIdOnCurrentPage();

  const ckey = useCustomerKey();
  const handleNavigateTo = useCallback(
    (group: ProcessGroup) => {
      let nextUrl;
      switch (true) {
        case isTier0Page:
          nextUrl = `${pageInUrl}?workSchedule=${workScheduleId}&process=${encodeURIComponent(
            group.processes[0].name
          )}`;
          break;
        case isOperatorPage:
          nextUrl = `${ckey}/dashboard/${group.summaryGroup}?workSchedule=${workScheduleId}`;
          break;
        default:
          nextUrl = `${pageInUrl}/${
            group.summaryGroup
          }?workSchedule=${workScheduleId}${isTimelinePage ? "&view=tl" : ""}`;
      }

      navigate(nextUrl);
      dispatch(clearAllTrendChartBars());
      dispatch(clearAllSegments());
      dispatch(clearAllAggregates());
      handleClose();
    },
    [
      dispatch,
      isTimelinePage,
      navigate,
      pageInUrl,
      workScheduleId,
      isTier0Page,
      isOperatorPage,
      ckey,
    ]
  );

  useCycleThroughProcessGroups();
  const [
    {
      general: { showProcessGroupSwitcherDropdownCycler },
    },
  ] = useFlightConfig();

  const { showPGSDropdown } = useBreadcrumbDropdowns();
  const { isTabletOrLarger } = useBreakpoints();
  const animationsEnabled = useAnimationsEnabled();
  const springLeftRight = useSpring({
    ...(animationsEnabled
      ? {
          transform: `translateX(${
            showPGSDropdown ? 0 : Math.min(500, 0.5 * innerWidth - 58 - 96)
          }px)`,
          marginLeft:
            showPGSDropdown && !isTabletOrLarger
              ? `-${BREADCRUMBS_GAP_REMS}rem`
              : "0rem",
        }
      : {}),
    opacity: showPGSDropdown ? 1 : 0,
    config: showPGSDropdown
      ? {
          /* default config */ tension: 170,
          friction: 26,
          mass: 1,
        }
      : {
          /* a bit slower on the way out */ tension: 100,
          friction: 30,
          mass: 1,
        },
  });
  const prevProcessGroupName = usePreviousDistinct(processGroupName);
  const isOnlyProcessGroup =
    allGroups.filter(
      group =>
        group.processes[0] &&
        group.processes[0].workScheduleConfig.id === workScheduleId
    ).length === 1;

  let processGroupNameDisplay = processGroupName || prevProcessGroupName;
  // edge case for work schedule name === process group name e.g. https://localhost:3000/sojo-industries/dashboard/Bristol?workSchedule=2
  const [workSchedulesConfigList] = useWorkScheduleConfigJsonListAtom();
  const currentWorkScheduleId = useWorkScheduleIdOnCurrentPage();
  const currentWorkScheduleConfig = workSchedulesConfigList.find(
    config => config.id === currentWorkScheduleId
  );
  const isDuplicateOfWorkScheduleName =
    currentWorkScheduleConfig &&
    processGroupNameDisplay === currentWorkScheduleConfig.name &&
    workSchedulesConfigList.length > 1;
  if (isDuplicateOfWorkScheduleName) {
    processGroupNameDisplay = translate(`All`);
  }
  const showPgSwitcherDropdownArrow = !isAllWorkSchedulesPage;
  const isMultiSchedulesPage = isAllWorkSchedulesPage && !isGroupTilesPage;
  if (isMultiSchedulesPage) {
    // get group names from query param "groups"
    const groups = qs.parse(search).groups;
    if (groups) {
      processGroupNameDisplay = String(groups).split(",").join(", ");
    }
  }
  return (
    <animated.div
      style={{ ...springLeftRight }}
      className="processgroupswitcher"
    >
      <ProcessGroupSwitcherStyles
        className={`name${isMultiSchedulesPage ? " multiSchedules" : ""}`}
        data-component-name={"Group Switcher Dropdown"}
      >
        <Tooltip
          title={
            isOnlyProcessGroup || !showPgSwitcherDropdownArrow
              ? ""
              : translate(`Switch_groups`)
          }
        >
          <Button
            aria-controls="group-switcher-menu"
            aria-haspopup="true"
            className="btnDropDown"
            onClick={
              isOnlyProcessGroup || !showPgSwitcherDropdownArrow
                ? () => null
                : handleClick
            }
            endIcon={
              isOnlyProcessGroup || !showPgSwitcherDropdownArrow ? null : (
                <DropdownIcon className="dropdownArrow" />
              )
            }
            disabled={isOnlyProcessGroup}
            style={!showPgSwitcherDropdownArrow ? { cursor: "default" } : {}}
          >
            <div className="departmentName tilesTimelinePages">
              <Tooltip
                title={
                  processGroupNameDisplay &&
                  processGroupNameDisplay.length > MAX_GROUP_NAME_LENGTH
                    ? processGroupNameDisplay
                    : ""
                }
              >
                <div>
                  {processGroupNameDisplay?.slice(0, MAX_GROUP_NAME_LENGTH) ??
                    ""}
                  {processGroupNameDisplay &&
                  processGroupNameDisplay.length > MAX_GROUP_NAME_LENGTH
                    ? "..."
                    : ""}
                </div>
              </Tooltip>
            </div>
          </Button>
        </Tooltip>
        <Menu
          id="group-switcher-menu"
          anchorEl={anchorEl}
          keepMounted={false}
          disableScrollLock={true}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          variant={"menu"} // Use "menu" to prevent selected items from impacting the initial focus and the vertical alignment relative to the anchor element.
        >
          {allGroups
            .filter(
              group =>
                group.processes[0] &&
                group.processes[0].workScheduleConfig.id === workScheduleId
            )
            .sort((a, b) => Number(a.groupSortIdx) - Number(b.groupSortIdx))
            .map((group, groupIdx) => {
              const active = [
                snakeCase(group.shortName),
                snakeCase(group.longName),
              ].includes(snakeCase(navTitle));
              return (
                <MenuItem
                  className={`group-switcher-menu-item ${snakeCase(
                    group.summaryGroup
                  )}`}
                  key={groupIdx}
                  disabled={active}
                  onClick={() => handleNavigateTo(group)}
                >
                  {isTabletOrLarger ? group.longName : group.shortName}
                </MenuItem>
              );
            })}
          {showProcessGroupSwitcherDropdownCycler && (
            <MenuItem
              onClick={() => {
                console.log("ProcessGroupSwitcherDropdown Cycler clicked");
                setCyclingThroughProcessGroups({
                  enabled: !enabled,
                  cycleTimeMs,
                });
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <Checkbox checked={enabled} />
                <div>{translate(`Cycle_through_groups_every`)}</div>
                <div>
                  <Input
                    style={{ width: 50, marginLeft: 5, marginRight: 5 }}
                    type="number"
                    inputProps={{ min: 5 }}
                    value={cycleTimeMs / 1000}
                    onClick={e => e.stopPropagation()}
                    onChange={e => {
                      e.stopPropagation();
                      setCyclingThroughProcessGroups({
                        enabled,
                        cycleTimeMs: Number(e.target.value) * 1000,
                      });
                    }}
                  />
                </div>
                <div>s</div>
              </div>
            </MenuItem>
          )}
        </Menu>
      </ProcessGroupSwitcherStyles>
    </animated.div>
  );
}

function useCycleThroughProcessGroups() {
  const [{ enabled, cycleTimeMs }] = useAtom(cyclingThroughProcessGroupsAtom);
  const allProcessGroups = useSelector(state => state.legacy.allGroups);
  const processGroup = useVisibleGroups()[0];
  useInterval({
    callback: () => {
      // open the switcher dropdown
      const dropdown =
        document.querySelector<HTMLElement>(`[data-component-name="Group Switcher Dropdown"
] .btnDropDown`);
      if (!dropdown) {
        return;
      }
      dropdown.click();
      setTimeout(() => {
        // literally just click the next menu item
        const currentGroupIdx = allProcessGroups.findIndex(
          group => group.summaryGroup === processGroup.summaryGroup
        );
        const nextGroupIdx = (currentGroupIdx + 1) % allProcessGroups.length;
        const nextGroup = allProcessGroups[nextGroupIdx];
        if (nextGroup) {
          const nextGroupMenuItem = document.querySelector<HTMLElement>(
            `.group-switcher-menu-item.${snakeCase(nextGroup.summaryGroup)}`
          );
          if (nextGroupMenuItem) {
            nextGroupMenuItem.click();
          }
        }
      }, 1000);
    },
    immediate: false,
    delay: enabled ? cycleTimeMs : null,
  });
}
